import { useCallback, useEffect, useMemo, useState } from 'react';
import { createApiClient, type ApiClient, type Principal } from '@gc/api-client';
import './styles/app.css';
import { Members } from './admin/Members.js';
import { Audit } from './admin/Audit.js';
import { Gadgets } from './gadgets/Gadgets.js';
import { Dashboard } from './dashboard/Dashboard.js';
import { MyDevices } from './dashboard/MyDevices.js';
import { createAuthClient } from './auth/client.js';
import { clear, isExpired, load, store, type Session } from './auth/session.js';
import { config } from './config.js';

const auth = createAuthClient({
  authDomain: config.authDomain,
  clientId: config.cognitoClientId,
  redirectUri: `${location.origin}/auth/callback`,
  logoutUri: `${location.origin}/`,
});

type State =
  | { kind: 'loading' }
  | { kind: 'signed_out' }
  | { kind: 'signed_in'; who: Principal; api: ApiClient }
  | { kind: 'error'; message: string };

type ViewId = 'mine' | 'dashboard' | 'gadgets' | 'people' | 'activity';

/**
 * The navigation, and the permission each entry needs.
 *
 * Entries are hidden when the permission is absent, which is a courtesy rather than the
 * security boundary — the API enforces the same check and would refuse the call regardless.
 * Screens were previously stacked on one page; with four of them that had stopped being
 * readable, so one is shown at a time.
 */
const VIEWS: { id: ViewId; label: string; permission: string | null }[] = [
  { id: 'dashboard', label: 'Dashboard', permission: 'gadgets:view' },
  { id: 'gadgets', label: 'Gadgets', permission: 'gadgets:view' },
  { id: 'people', label: 'People', permission: 'users:manage' },
  { id: 'activity', label: 'Activity', permission: 'audit:view' },
  // No permission: /v1/dashboard/mine is the one route needing none, because everybody has
  // their own devices. Without it an individual signed in to an empty page — every other screen
  // requires a permission they do not have.
  //
  // Last on purpose. The opening view is the first available one, so an administrator still
  // lands on the fleet dashboard while an individual, for whom this is the only entry, lands
  // here. That falls out of the ordering rather than needing a rule.
  { id: 'mine', label: 'My devices', permission: null },
];

/**
 * The federated providers this pool actually has, written into /config.js by Terraform from the
 * app client's own list. This is the authority on which options can be used, as opposed to which
 * are offered.
 */
const federated = config.identityProviders.filter((p) => p !== 'COGNITO');

/**
 * The federated options the sign-in screen offers, whether or not the pool has them yet.
 *
 * These were briefly hidden when unconfigured, because a live "Continue with Google" against a
 * pool with no Google provider sends the visitor to a Cognito error page. Showing the options is
 * the product decision; landing somebody on that error page is not. So an option whose provider
 * is missing renders disabled and says why, and turns itself on the moment the provider exists —
 * no code change, because the list comes from the Cognito app client itself.
 *
 * GitHub was offered here and has been removed. Cognito federates via SAML, OIDC, or a fixed set
 * of social providers, and GitHub is none of them: its login is OAuth 2.0 only, with no OIDC
 * discovery document and no id_token, so there is nothing to federate with. Supporting it means
 * running an OIDC shim — a service that is itself an identity provider, where a bug is an
 * authentication bypass. For a product bought by IT and facilities teams that is the wrong
 * trade, and a button reading "not yet available" forever is worse than no button.
 */
const OFFERED = ['Google'] as const;

/** The mark from gc-brand-elements, drawn with tokens so it tracks the palette. */
function Lockup() {
  return (
    <a className="lockup" href="/" aria-label="GadgetCloud home">
      <svg className="lockup-mark" viewBox="0 0 100 100" aria-hidden="true">
        <rect width="100" height="100" rx="22" fill="var(--color-primary)" />
        <path d="M50 24h22a4 4 0 0 1 4 4v22a8 8 0 0 1-2.34 5.66L52.5 77.16a6 6 0 0 1-8.49 0L22.84 56a6 6 0 0 1 0-8.49l21.5-21.17A8 8 0 0 1 50 24z" fill="#fff" />
        <circle cx="64" cy="36" r="6" fill="var(--color-primary)" />
      </svg>
      <span className="lockup-word">Gadget<span>Cloud</span></span>
    </a>
  );
}


/**
 * The sign-in page.
 *
 * Its own page rather than a card under the app header: somebody who is not signed in has no
 * application to frame, and the first thing they should see is what this is and how to get in.
 *
 * The address is collected here and passed to Cognito as login_hint, so it is typed once rather
 * than again on the hosted page.
 */
function SignIn({ problem }: { problem?: string }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const go = (idp?: string, hint?: string) => {
    setBusy(true);
    setFailure(null);
    void auth.authorizeUrl(idp, hint)
      .then((u) => location.assign(u))
      .catch((e: unknown) => {
        // Building the URL needs crypto and session storage. A locked-down browser can refuse
        // both, and leaving the button spinning would be the least useful outcome.
        setBusy(false);
        setFailure(e instanceof Error ? e.message : 'could not start sign-in');
      });
  };

  return (
    <main className="signin-page">
      <div className="signin-card">
        <svg className="signin-mark" viewBox="0 0 100 100" aria-hidden="true">
          <rect width="100" height="100" rx="22" fill="var(--color-primary)" />
          <path d="M50 24h22a4 4 0 0 1 4 4v22a8 8 0 0 1-2.34 5.66L52.5 77.16a6 6 0 0 1-8.49 0L22.84 56a6 6 0 0 1 0-8.49l21.5-21.17A8 8 0 0 1 50 24z" fill="#fff" />
          <circle cx="64" cy="36" r="6" fill="var(--color-primary)" />
        </svg>

        <h1>Sign in</h1>
        <p className="signin-sub">
          We&rsquo;ll email you a one-time code. There is no password to remember, and none is
          stored anywhere.
        </p>

        {(problem ?? failure) && (
          <p className="signin-problem" role="alert" data-testid="signin-problem">
            {problem ?? failure}
          </p>
        )}

        <form
          className="signin-form"
          onSubmit={(e) => { e.preventDefault(); go(undefined, email.trim() || undefined); }}
        >
          <label htmlFor="signin-email-input">Email address</label>
          <input
            id="signin-email-input"
            data-testid="signin-email-input"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button className="primary" type="submit" data-testid="signin-email" disabled={busy}>
            {busy ? 'Taking you to sign in…' : 'Send code'}
          </button>
        </form>

        <div className="signin-or"><span>or</span></div>
        <div className="signin-federated">
          {/* Anything the pool actually has, plus the two we intend to offer, in that order and
              without repeating one that appears in both. */}
          {[...federated, ...OFFERED.filter((p) => !federated.includes(p))].map((p) => {
            const ready = federated.includes(p);
            return (
              <button
                key={p}
                type="button"
                data-testid={`signin-${p.toLowerCase()}`}
                disabled={busy || !ready}
                aria-describedby={ready ? undefined : `signin-${p.toLowerCase()}-note`}
                onClick={() => go(p)}
              >
                Continue with {p}
                {!ready && <span className="signin-soon" id={`signin-${p.toLowerCase()}-note`}>Not yet available</span>}
              </button>
            );
          })}
        </div>
      </div>

      <p className="signin-foot">GadgetCloud — enterprise gadget lifecycle management</p>
    </main>
  );
}

/**
 * The application shell. Authentication is delegated entirely to Cognito — this app collects an
 * email address to pass along as login_hint and nothing else. No credential is ever typed here,
 * and there is no password to type.
 */
export function App() {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [view, setView] = useState<ViewId | null>(null);

  const available = useMemo(
    () => (state.kind === 'signed_in'
      ? VIEWS.filter((v) => v.permission === null || state.who.permissions.includes(v.permission as never))
      : []),
    [state],
  );
  // Whatever they can actually see, rather than a fixed default that might be forbidden.
  const current = view ?? available[0]?.id ?? null;

  const signOut = useCallback(() => {
    clear();
    location.assign(auth.logoutUrl());
  }, []);

  useEffect(() => {
    let live = true;

    void (async () => {
      try {
        // Returning from the identity provider.
        if (location.pathname === '/auth/callback') {
          const session = await auth.handleCallback(location.search);
          store(session);
          history.replaceState(null, '', '/');
        }

        let session: Session | null = load();
        if (session && isExpired(session) && session.refreshToken) {
          try {
            session = await auth.refresh(session.refreshToken);
            store(session);
          } catch {
            clear(); // a refresh token that no longer works means the session is over
            session = null;
          }
        }
        if (!session || isExpired(session)) {
          if (live) setState({ kind: 'signed_out' });
          return;
        }

        const api = createApiClient({ baseUrl: config.apiUrl, getToken: () => session!.accessToken });
        const who = await api.me();
        if (live) setState({ kind: 'signed_in', who, api });
      } catch (e) {
        if (live) setState({ kind: 'error', message: e instanceof Error ? e.message : 'unknown error' });
      }
    })();

    return () => { live = false; };
  }, []);

  if (state.kind === 'signed_out') return <SignIn />;
  if (state.kind === 'error') return <SignIn problem={`We could not sign you in: ${state.message}`} />;

  return (
    <>
      <header className="app-head">
        <div className="app-head-inner">
          <Lockup />

          {state.kind === 'signed_in' && (
            <>
              <nav className="app-nav" aria-label="Sections">
                {available.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    data-testid={`nav-${v.id}`}
                    aria-current={current === v.id ? 'page' : undefined}
                    onClick={() => setView(v.id)}
                  >
                    {v.label}
                  </button>
                ))}
              </nav>

              <div className="app-who">
                {/* The subject claim, truncated by CSS: it identifies the session without
                    dominating the header. */}
                <span data-testid="signed-in" title={state.who.sub}>{state.who.sub}</span>
                <button data-testid="signout" onClick={signOut}>Sign out</button>
              </div>
            </>
          )}
        </div>
      </header>

      <main className="app-main">
        {/* A page needs a name. The lockup is a link and each screen's heading names a section
            within the page rather than the page itself, so this was left with no h1 at all when
            the shell was introduced. Hidden except when signing in, where it is the visible
            heading and there is nothing else for it to duplicate. */}
        {/* Named for the page, not the section. "People" alone would repeat the heading that
            screen already renders, which a screen reader reads out twice. */}
        {state.kind === 'signed_in' && (
          <h1 className="visually-hidden">
            GadgetCloud — {available.find((v) => v.id === current)?.label ?? 'Home'}
          </h1>
        )}
        {state.kind !== 'signed_in' && <h1 className="visually-hidden">GadgetCloud</h1>}

        {state.kind === 'loading' && <p>Checking your session…</p>}


        {state.kind === 'signed_in' && (
          <>
            {current === 'mine' && (
              <MyDevices api={state.api} hasOrg={state.who.orgId !== null} />
            )}
            {current === 'dashboard' && (
              <Dashboard api={state.api} canExport={state.who.permissions.includes('data:export')} />
            )}
            {current === 'gadgets' && <Gadgets api={state.api} permissions={state.who.permissions} />}
            {current === 'people' && <Members api={state.api} />}
            {current === 'activity' && <Audit api={state.api} />}

            {state.who.orgId === null && current !== 'mine' && (
              <p data-testid="no-org">
                You are signed in as an individual. Organisation management appears once you belong to one.
              </p>
            )}
          </>
        )}

      </main>
    </>
  );
}
